import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import ArticleIcon from '@mui/icons-material/Article';
import { Button, Pagination } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import { hrOfficeExpenseCleanUp, hrOffliceExpenses } from 'app/redux/actions/subTaskAction';
import subTaskService from 'app/services/subtask_service';
import moment from 'moment';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useSearchParams } from 'react-router-dom';
import ExpenseDocumentBox from './ExpenseDocumentBox';
import OfficeExpenseForm from './OfficeExpenseForm';
import Swal from 'sweetalert2';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PaymentStatusForm from './PaymentStatusForm';
import { docBaseUrl } from 'app/services/config';

// Constants
const EXPENSE_LIMIT = 10;
const CHIP_COLORS = {
  'Requested': 'blue',
  'Approved': 'green',
  'Rejected': 'red',
  'default': 'gray'
};

// Helper function to create filter query string from DataGrid's filter model
const createFilterString = (filterModel) => {
  if (!filterModel?.items) return '';
  return filterModel.items
    .filter(item => item.field !== undefined)
    .map(item => `&${item.field}=${item.value || ''}`)
    .join('');
};

// Helper function for reusable SweetAlert
const sweetAlerts = (variant, message, callback) => {
  Swal.fire({
    icon: variant,
    title: variant === "success" ? 'Success' : 'Warning',
    text: message,
    showCancelButton: variant !== "success",
    confirmButtonText: 'OK',
    cancelButtonText: 'Cancel'
  }).then((result) => {
    if (result.isConfirmed && callback) {
      callback();
    }
  });
};

export default function HrOfficeExpense() {
  const dispatch = useDispatch();
  const { setAuthToken } = useJumboAuth();
  const roleCode = localStorage.getItem("roleCode");
  const { hrOfficeExpensesData } = useSelector(state => state.typereducer);
  const { hideDialog, showDialog } = useJumboDialog();
  const [totalCount, setTotalCount] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL query parameters
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialFilterModel = JSON.parse(searchParams.get('filter') || '{}');
  const [page, setPage] = React.useState(initialPage);
  const [queryOptions, setQueryOptions] = React.useState({ filterModel: initialFilterModel });

  // Memoized rows to avoid re-computation on every render
  const rows = React.useMemo(() => {
    let _id = (page - 1) * EXPENSE_LIMIT + 1;
    return hrOfficeExpensesData.map(item => ({
      id: _id++,
      employeeName: item?.employee_id?.name || '--',
      expenseId: item?.id,
      expenseAmount: item?.expense_amount,
      expenseDate: item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '',
      expenseDescription: item?.expense_description || '--',
      expenseNote: item?.expense_note || '--',
      expenseDoc: item?.bill_reciept_documents || [],
      status: item?.status || 'Requested',
      hrFinanceStatus: item?.hr_finance_status || '--',
      paymentStatus: item?.payment_status || 'Unpaid',
      paymentDate: item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
      actionBy: item?.action_by?.name || '--',
      addedBy: item?.created_by?.name || '--',
      actionRemark: item?.action_remark || '--',
      category: item?.category || '--',
      company: item?.company || '--',
      projects: item?.projects || '--',
    }));
  }, [hrOfficeExpensesData, page]);

  // Handler for opening expense document dialog
  const showExpenseDialog = React.useCallback((expenseDoc) => {
    showDialog({
      title: "Expense Documents",
      content: <ExpenseDocumentBox expenseDoc={expenseDoc} />
    });
  }, [showDialog]);

  // Callback to handle dialog updates and close
  const handleDialogUpdate = React.useCallback(() => {
    hideDialog();
  }, [hideDialog]);

  // Handler for opening expense edit/update dialog
  const showAddEditExpenseDialog = React.useCallback((expenseId) => {
    showDialog({
      title: expenseId ? "Update Expense" : "Add Expense",
      content: <OfficeExpenseForm expanseId={expenseId} onSave={handleDialogUpdate} />
    });
  }, [handleDialogUpdate, showDialog]);

  // Handler for opening payment status update dialog
  const showPaymentStatusDialog = React.useCallback((expenseId, paymentStatus, paymentDate, status) => {
    showDialog({
      title: "Update Payment Status",
      content: <PaymentStatusForm expanseId={expenseId} paymentStatus={paymentStatus} paymentDate={paymentDate} status={status} onSave={handleDialogUpdate} />
    });
  }, [handleDialogUpdate, showDialog]);

  // Handler to cancel an expense request
  const cancleExpenseRequest = (expanseId) => {
    sweetAlerts("warning", "Are you sure you want to cancel this expense?", () => {
      subTaskService.updateExpenseStatus({ status: "Cancelled" }, expanseId)
        .then(() => {
          sweetAlerts('success', 'Expense Cancelled Successfully!', () => {
            window.location.reload();
          });
        })
        .catch((err) => {
          hideDialog();
          if (err?.response?.status === 401) {
            setAuthToken(null);
          } else {
            sweetAlerts('error', 'Failed to cancel expense');
          }
        });
    });
  };

  // Handler for DataGrid's filter changes
  const onFilterChange = React.useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });
    if (filterModel?.items?.[0]?.value !== undefined) {
      setPage(1); // Reset to page 1 on filter change
    }
  }, []);

  // Handler for pagination changes
  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  // Handler for DataGrid cell clicks
  const handleCellClick = (params) => {
    const { field, value } = params;
    if (!['id', '__check__', 'expenseDoc'].includes(field)) {
      Swal.fire({
        html: `<div style="white-space: pre-wrap;">${value}</div>`,
        showConfirmButton: false,
        customClass: { popup: 'my-custom-size' },
        didOpen: () => {
          const swalPopup = Swal.getPopup();
          if (swalPopup) swalPopup.style.width = '400px';
        },
      });
    }
  };

  // Effect to fetch data and update URL
  React.useEffect(() => {
    // Sync state to URL search parameters
    const params = {};
    if (page !== 1) {
      params.page = page;
    }
    if (Object.keys(queryOptions.filterModel).length > 0) {
      params.filter = JSON.stringify(queryOptions.filterModel);
    }
    setSearchParams(params, { replace: true });

    // Fetch data from API
    const offset = (page - 1) * EXPENSE_LIMIT;
    const filterString = createFilterString(queryOptions.filterModel);

    subTaskService.getHrOfficeExpenses(offset, EXPENSE_LIMIT, filterString)
      .then((data) => {
        setTotalCount(data?.data?.count);
        dispatch(hrOffliceExpenses(data?.data?.data));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setAuthToken(null);
        }
      });

    return () => {
      dispatch(hrOfficeExpenseCleanUp());
    };
  }, [page, queryOptions, dispatch, setAuthToken, setSearchParams]);

  // Memoized column definitions
  const columns = React.useMemo(() => [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      filterable: false,
      flex: 100,
      maxWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isEditable = !['Cancelled', 'Approved', 'Rejected'].includes(params.row.status);
        const isDeletable = params.row.status === 'Requested';
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span>{params.value}</span>
            <NavLink>
              <EditIcon
                onClick={() => isEditable && showAddEditExpenseDialog(params.row.expenseId)}
                style={{
                  width: '20px', height: '20px', marginLeft: '5px',
                  cursor: isEditable ? 'pointer' : 'not-allowed'
                }}
                color="action"
                disabled={!isEditable}
              />
            </NavLink>
            <NavLink>
              <DeleteIcon
                onClick={() => isDeletable && cancleExpenseRequest(params.row.expenseId)}
                style={{
                  width: '20px', height: '20px', marginLeft: '5px',
                  cursor: isDeletable ? 'pointer' : 'not-allowed'
                }}
                color="action"
                disabled={!isDeletable}
              />
            </NavLink>
          </div>
        );
      },
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>ID</div>)
    },
    { field: 'employeeName', headerName: 'Employee Name', sortable: false, flex: 220, align: 'center', headerAlign: 'center' },
    {
      field: 'status',
      headerName: 'Manager Status',
      sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 10, height: 10, backgroundColor: CHIP_COLORS[params.value] || CHIP_COLORS.default, marginRight: 8, borderRadius: '100%' }}></div>
          {params.value}
        </div>
      )
    },
    {
      field: 'hrFinanceStatus',
      headerName: 'HR/Finance Status',
      sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 10, height: 10, backgroundColor: CHIP_COLORS[params.value] || CHIP_COLORS.default, marginRight: 8, borderRadius: '100%' }}></div>
          {params.value}
        </div>
      )
    },
    { field: 'actionRemark', headerName: 'Remark', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'actionBy', headerName: 'Approved By', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'expenseAmount', headerName: 'Expense Amount', sortable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'category', headerName: 'Category', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'projects', headerName: 'Projects', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'company', headerName: 'Company', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'expenseDate', headerName: 'Expense Date', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center' },
    { field: 'expenseDescription', headerName: 'Description', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'expenseNote', headerName: 'Note', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'paymentStatus', headerName: 'Payment Status', type: 'singleSelect', valueOptions: ['Paid', 'Unpaid'], sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      type: 'date',
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? null : date;
      }
    },
    {
      field: 'expenseDoc',
      headerName: 'Documents',
      filterable: false,
      sortable: false,
      flex: 300,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params.row.expenseDoc.length > 0 && (
            <ArticleIcon
              onClick={() => showExpenseDialog(params.value)}
              style={{ width: '20px', height: '20px', marginLeft: '5px' }}
              color="action"
            />
          )}
        </div>
      )
    },
  ], [showAddEditExpenseDialog, cancleExpenseRequest, showExpenseDialog]);


  return (
    <>
      <div style={{ height: '60px', width: '100%' }}>
        <Button
          sx={{ float: 'right' }}
          variant="contained"
          onClick={() => showAddEditExpenseDialog()}
        >
          Add Expense
        </Button>
      </div>
      <Box sx={{ height: '100%', width: '100%' }}>
        <DataGridPro
          sx={{ backgroundColor: '#F6F4FA' }}
          rows={rows}
          columns={columns}
          pageSize={EXPENSE_LIMIT}
          autoPageSize
          autoHeight
          density='standard'
          filterMode='server'
          onFilterModelChange={onFilterChange}
          hideFooter
          onCellClick={handleCellClick}
        />
        <Pagination
          count={Math.ceil(totalCount / EXPENSE_LIMIT)}
          sx={{ mt: 3, float: 'right', color: "#F6F4FA" }}
          page={page}
          onChange={handlePaginationChange}
        />
      </Box>
    </>
  );
}